import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { authApi } from '../api/auth'
import { useAuthStore } from '../stores/authStore'
import { getErrorMessage } from '@/shared/api/client'
import { api } from '@/shared/api/generated'
import { toast } from '@/shared/stores/toastStore'
import { TOTPInput } from './TOTPInput'

export function LoginForm() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [requires2FA, setRequires2FA] = useState(false)
  const [tempToken, setTempToken] = useState('')
  const [webauthnAvailable, setWebauthnAvailable] = useState(false)
  const [siteName, setSiteName] = useState('')
  const [hasLogo, setHasLogo] = useState(false)
  
  const navigate = useNavigate()
  const login = useAuthStore((state) => state.login)

  useEffect(() => {
    api.getBranding().then(b => {
      setSiteName(b.site_name)
      setHasLogo(b.has_logo)
    }).catch(err => toast.error(getErrorMessage(err)))
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    if (!username || !password) {
      setError('Username and password are required')
      return
    }

    setLoading(true)
    try {
      const response = await authApi.login(username, password)
      
      if ('requires_2fa' in response && response.requires_2fa) {
        setRequires2FA(true)
        setTempToken(response.temp_token)
        setWebauthnAvailable(!!response.webauthn_available)
      } else if ('token' in response) {
        login(response.token, { id: 0, username: '', isUserAdmin: false })
        const session = await authApi.getSession()
        login(response.token, authApi.sessionToUser(session))
        navigate('/files')
      }
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handle2FASuccess = (token: string, user: any) => {
    login(token, user)
    navigate('/files')
  }

  if (requires2FA) {
    return <TOTPInput tempToken={tempToken} webauthnAvailable={webauthnAvailable} onSuccess={handle2FASuccess} />
  }

  return (
    <div className="login-page">
      <div className="card login-card">
        {hasLogo && <img src="/branding/logo.svg" alt="" className="login-logo" />}
        <h2>Sign in{siteName ? ` to ${siteName}` : ''}</h2>
        
        <form onSubmit={handleSubmit} autoComplete="off">
          {error && <div className="alert error">{error}</div>}
          
          <div className="form-fields">
            <div className="field">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                autoComplete="off"
              />
            </div>
            
            <div className="field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoComplete="new-password"
              />
            </div>
          </div>

          <button type="submit" className="btn-primary full-width" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}
